<template>
  <b-card no-body>
    <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
      <b-card-title class="mb-0">
        계좌
      </b-card-title>
      <b-button
        variant="primary"
        :disabled="loadingAccounts || !selectedAccount"
        @click="refreshActiveTab"
      >
        <b-spinner
          v-if="activeLoading"
          small
          class="mr-50"
        />
        새로고침
      </b-button>
    </b-card-header>

    <b-card-body>
      <b-alert
        v-if="accountErrorMessage"
        variant="danger"
        show
      >
        {{ accountErrorMessage }}
      </b-alert>

      <div class="d-flex flex-wrap align-items-end mb-2 account-filter-row">
        <div class="mr-1 mb-50">
          <label class="form-label d-block mb-25">계좌번호조회</label>
          <b-form-select
            v-model="selectedAccount"
            :options="accountOptions"
            :disabled="loadingAccounts || accountOptions.length === 0"
            style="min-width: 220px;"
            @change="handleAccountChange"
          />
        </div>
        <div class="mb-50">
          <label class="form-label d-block mb-25">조회일자</label>
          <b-form-input
            v-model="queryDate"
            type="date"
            style="width: 180px;"
          />
        </div>
      </div>

      <b-tabs
        v-model="activeTabIndex"
        pills
        nav-class="nav-pill-primary mb-2"
        @input="handleTabChange"
      >
        <b-tab title="일별잔고수익률">
          <b-alert
            v-if="dailyErrorMessage"
            variant="danger"
            show
          >
            {{ dailyErrorMessage }}
          </b-alert>

          <div
            v-if="dailyLoading"
            class="text-center py-2"
          >
            <b-spinner />
          </div>

          <template v-else>
            <div class="daily-summary-grid mb-2">
              <div class="summary-box">
                <small class="text-muted d-block">총 매입가</small>
                <strong>{{ formatNumber(dailySummary.totalBuyAmount) }}</strong>
              </div>
              <div class="summary-box">
                <small class="text-muted d-block">총 평가금액</small>
                <strong>{{ formatNumber(dailySummary.totalEvalAmount) }}</strong>
              </div>
              <div class="summary-box">
                <small class="text-muted d-block">총 평가손익</small>
                <strong :class="rateClassByValue(dailySummary.totalEvalProfit)">{{ formatSignedNumber(dailySummary.totalEvalProfit) }}</strong>
              </div>
              <div class="summary-box">
                <small class="text-muted d-block">수익률</small>
                <strong :class="rateClassByValue(dailySummary.totalProfitRate)">{{ formatRate(dailySummary.totalProfitRate) }}</strong>
              </div>
              <div class="summary-box">
                <small class="text-muted d-block">예수금</small>
                <strong>{{ formatNumber(dailySummary.depositBalance) }}</strong>
              </div>
              <div class="summary-box">
                <small class="text-muted d-block">추정자산</small>
                <strong>{{ formatNumber(dailySummary.estimatedAsset) }}</strong>
              </div>
            </div>

            <b-table
              :items="dailyItems"
              :fields="dailyFields"
              responsive
              small
              show-empty
              empty-text="조회된 잔고수익률이 없습니다."
            >
              <template #cell(currentPrice)="row">
                {{ formatNumber(row.item.currentPrice) }}
              </template>
              <template #cell(remainQuantity)="row">
                {{ formatNumber(row.item.remainQuantity) }}
              </template>
              <template #cell(buyUnitPrice)="row">
                {{ formatNumber(row.item.buyUnitPrice) }}
              </template>
              <template #cell(evalProfit)="row">
                <span :class="rateClassByValue(row.item.evalProfit)">{{ formatSignedNumber(row.item.evalProfit) }}</span>
              </template>
              <template #cell(profitRate)="row">
                <span :class="rateClassByValue(row.item.profitRate)">{{ formatRate(row.item.profitRate) }}</span>
              </template>
              <template #cell(evalAmount)="row">
                {{ formatNumber(row.item.evalAmount) }}
              </template>
            </b-table>

            <div class="d-flex justify-content-center mt-2">
              <b-button
                v-if="dailyHasMore"
                variant="outline-primary"
                :disabled="dailyLoadingMore"
                @click="loadMoreDaily"
              >
                <b-spinner
                  v-if="dailyLoadingMore"
                  small
                  class="mr-50"
                />
                더 불러오기
              </b-button>
            </div>
          </template>
        </b-tab>

        <b-tab title="미체결요청">
          <b-alert
            v-if="openOrderErrorMessage"
            variant="danger"
            show
          >
            {{ openOrderErrorMessage }}
          </b-alert>

          <div
            v-if="openOrderLoading"
            class="text-center py-2"
          >
            <b-spinner />
          </div>

          <template v-else>
            <b-table
              :items="openOrderItems"
              :fields="openOrderFields"
              responsive
              small
              show-empty
              empty-text="조회된 미체결이 없습니다."
            >
              <template #cell(orderQuantity)="row">
                {{ formatNumber(row.item.orderQuantity) }}
              </template>
              <template #cell(orderPrice)="row">
                {{ formatNumber(row.item.orderPrice) }}
              </template>
              <template #cell(openQuantity)="row">
                {{ formatNumber(row.item.openQuantity) }}
              </template>
              <template #cell(contractQuantity)="row">
                {{ formatNumber(row.item.contractQuantity) }}
              </template>
              <template #cell(currentPrice)="row">
                <span :class="rateClassBySign(row.item.currentPrice)">{{ formatSignedNumber(row.item.currentPrice) }}</span>
              </template>
            </b-table>

            <div class="d-flex justify-content-center mt-2">
              <b-button
                v-if="openOrderHasMore"
                variant="outline-primary"
                :disabled="openOrderLoadingMore"
                @click="loadMoreOpenOrders"
              >
                <b-spinner
                  v-if="openOrderLoadingMore"
                  small
                  class="mr-50"
                />
                더 불러오기
              </b-button>
            </div>
          </template>
        </b-tab>

        <b-tab title="체결요청">
          <b-alert
            v-if="filledOrderErrorMessage"
            variant="danger"
            show
          >
            {{ filledOrderErrorMessage }}
          </b-alert>

          <div
            v-if="filledOrderLoading"
            class="text-center py-2"
          >
            <b-spinner />
          </div>

          <template v-else>
            <b-table
              :items="filledOrderItems"
              :fields="filledOrderFields"
              responsive
              small
              show-empty
              empty-text="조회된 체결내역이 없습니다."
            >
              <template #cell(orderPrice)="row">
                {{ formatNumber(row.item.orderPrice) }}
              </template>
              <template #cell(orderQuantity)="row">
                {{ formatNumber(row.item.orderQuantity) }}
              </template>
              <template #cell(contractPrice)="row">
                {{ formatNumber(row.item.contractPrice) }}
              </template>
              <template #cell(contractQuantity)="row">
                {{ formatNumber(row.item.contractQuantity) }}
              </template>
              <template #cell(openQuantity)="row">
                {{ formatNumber(row.item.openQuantity) }}
              </template>
              <template #cell(fee)="row">
                {{ formatNumber(row.item.fee) }}
              </template>
              <template #cell(tax)="row">
                {{ formatNumber(row.item.tax) }}
              </template>
            </b-table>

            <div class="d-flex justify-content-center mt-2">
              <b-button
                v-if="filledOrderHasMore"
                variant="outline-primary"
                :disabled="filledOrderLoadingMore"
                @click="loadMoreFilledOrders"
              >
                <b-spinner
                  v-if="filledOrderLoadingMore"
                  small
                  class="mr-50"
                />
                더 불러오기
              </b-button>
            </div>
          </template>
        </b-tab>
      </b-tabs>
    </b-card-body>
  </b-card>
</template>

<script>
import axios from '@axios'
import {
  BAlert,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BFormInput,
  BFormSelect,
  BSpinner,
  BTab,
  BTable,
  BTabs,
} from 'bootstrap-vue'

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BFormInput,
    BFormSelect,
    BSpinner,
    BTab,
    BTable,
    BTabs,
  },
  data() {
    return {
      loadingAccounts: false,
      accountErrorMessage: '',
      accounts: [],
      selectedAccount: '',
      activeTabIndex: 0,
      queryDate: this.todayInput(),

      dailyLoading: false,
      dailyLoadingMore: false,
      dailyErrorMessage: '',
      dailySummary: {
        totalBuyAmount: '',
        totalEvalAmount: '',
        totalEvalProfit: '',
        totalProfitRate: '',
        depositBalance: '',
        estimatedAsset: '',
      },
      dailyItems: [],
      dailyHasMore: false,
      dailyNextKey: '',

      openOrderLoading: false,
      openOrderLoadingMore: false,
      openOrderErrorMessage: '',
      openOrderItems: [],
      openOrderHasMore: false,
      openOrderNextKey: '',

      filledOrderLoading: false,
      filledOrderLoadingMore: false,
      filledOrderErrorMessage: '',
      filledOrderItems: [],
      filledOrderHasMore: false,
      filledOrderNextKey: '',

      dailyFields: [
        { key: 'stockCode', label: '종목코드' },
        { key: 'stockName', label: '종목명' },
        { key: 'currentPrice', label: '현재가' },
        { key: 'remainQuantity', label: '보유수량' },
        { key: 'buyUnitPrice', label: '매입단가' },
        { key: 'buyWeight', label: '매수비중' },
        { key: 'evalProfit', label: '평가손익' },
        { key: 'profitRate', label: '수익률' },
        { key: 'evalAmount', label: '평가금액' },
        { key: 'evalWeight', label: '평가비중' },
      ],
      openOrderFields: [
        { key: 'orderNo', label: '주문번호' },
        { key: 'stockName', label: '종목명' },
        { key: 'orderStatus', label: '주문상태' },
        { key: 'orderTypeName', label: '주문구분' },
        { key: 'tradeType', label: '매매구분' },
        { key: 'orderQuantity', label: '주문수량' },
        { key: 'orderPrice', label: '주문가격' },
        { key: 'openQuantity', label: '미체결수량' },
        { key: 'contractQuantity', label: '체결량' },
        { key: 'currentPrice', label: '현재가' },
        { key: 'time', label: '시간' },
        { key: 'exchangeTypeText', label: '거래소' },
      ],
      filledOrderFields: [
        { key: 'orderNo', label: '주문번호' },
        { key: 'stockName', label: '종목명' },
        { key: 'orderTypeName', label: '주문구분' },
        { key: 'orderStatus', label: '주문상태' },
        { key: 'tradeType', label: '매매구분' },
        { key: 'orderQuantity', label: '주문수량' },
        { key: 'contractQuantity', label: '체결량' },
        { key: 'contractPrice', label: '체결가' },
        { key: 'openQuantity', label: '미체결수량' },
        { key: 'fee', label: '수수료' },
        { key: 'tax', label: '세금' },
        { key: 'orderTime', label: '주문시간' },
        { key: 'exchangeTypeText', label: '거래소' },
      ],
    }
  },
  computed: {
    accountOptions() {
      return this.accounts.map(account => ({
        value: account,
        text: account,
      }))
    },
    activeLoading() {
      if (this.activeTabIndex === 0) return this.dailyLoading || this.dailyLoadingMore
      if (this.activeTabIndex === 1) return this.openOrderLoading || this.openOrderLoadingMore
      return this.filledOrderLoading || this.filledOrderLoadingMore
    },
  },
  created() {
    this.fetchAccounts()
  },
  methods: {
    parseApiResponse(response) {
      const payload = response && response.data ? response.data : null
      if (!payload || payload.success !== true) {
        throw new Error(payload && payload.error ? payload.error : '요청 처리 중 오류가 발생했습니다.')
      }
      return payload.data
    },
    todayInput() {
      const now = new Date()
      const yyyy = now.getFullYear()
      const mm = String(now.getMonth() + 1).padStart(2, '0')
      const dd = String(now.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },
    compactDate(value) {
      return (value || '').replace(/-/g, '')
    },
    async fetchAccounts() {
      this.loadingAccounts = true
      this.accountErrorMessage = ''
      try {
        const response = await axios.post('/api/kiwoom/account/list', {})
        const data = this.parseApiResponse(response)
        this.accounts = Array.isArray(data.accounts) ? data.accounts : []
        this.selectedAccount = this.accounts[0] || ''
        if (this.selectedAccount) {
          await this.fetchActiveTab()
        }
      } catch (error) {
        this.accounts = []
        this.selectedAccount = ''
        this.accountErrorMessage = error && error.message ? error.message : '계좌번호 조회에 실패했습니다.'
      } finally {
        this.loadingAccounts = false
      }
    },
    async handleAccountChange() {
      await this.fetchActiveTab()
    },
    async handleTabChange() {
      await this.fetchActiveTab()
    },
    async refreshActiveTab() {
      await this.fetchActiveTab()
    },
    async fetchActiveTab() {
      if (!this.selectedAccount) return
      if (this.activeTabIndex === 0) {
        await this.fetchDailyBalance()
        return
      }
      if (this.activeTabIndex === 1) {
        await this.fetchOpenOrders()
        return
      }
      await this.fetchFilledOrders()
    },
    async fetchDailyBalance() {
      this.dailyLoading = true
      this.dailyErrorMessage = ''
      this.dailyHasMore = false
      this.dailyNextKey = ''
      try {
        const data = await this.requestDailyBalance('N', '')
        this.applyDailyResponse(data, false)
      } catch (error) {
        this.dailyItems = []
        this.dailySummary = this.emptyDailySummary()
        this.dailyErrorMessage = error && error.message ? error.message : '일별잔고수익률 조회에 실패했습니다.'
      } finally {
        this.dailyLoading = false
      }
    },
    async loadMoreDaily() {
      if (!this.dailyHasMore || !this.dailyNextKey) return
      this.dailyLoadingMore = true
      try {
        const data = await this.requestDailyBalance('Y', this.dailyNextKey)
        this.applyDailyResponse(data, true)
      } catch (error) {
        this.dailyErrorMessage = error && error.message ? error.message : '일별잔고수익률 추가 조회에 실패했습니다.'
      } finally {
        this.dailyLoadingMore = false
      }
    },
    async requestDailyBalance(contYn, nextKey) {
      const response = await axios.post('/api/kiwoom/account/daily-balance', {
        accountNo: this.selectedAccount,
        queryDate: this.compactDate(this.queryDate),
        contYn,
        nextKey,
      })
      return this.parseApiResponse(response)
    },
    applyDailyResponse(data, append) {
      this.dailySummary = {
        totalBuyAmount: data.totalBuyAmount,
        totalEvalAmount: data.totalEvalAmount,
        totalEvalProfit: data.totalEvalProfit,
        totalProfitRate: data.totalProfitRate,
        depositBalance: data.depositBalance,
        estimatedAsset: data.estimatedAsset,
      }
      const items = Array.isArray(data.items) ? data.items : []
      this.dailyItems = append ? this.dailyItems.concat(items) : items
      this.dailyHasMore = !!data.hasMore
      this.dailyNextKey = data.nextKey || ''
    },
    emptyDailySummary() {
      return {
        totalBuyAmount: '',
        totalEvalAmount: '',
        totalEvalProfit: '',
        totalProfitRate: '',
        depositBalance: '',
        estimatedAsset: '',
      }
    },
    async fetchOpenOrders() {
      this.openOrderLoading = true
      this.openOrderErrorMessage = ''
      this.openOrderHasMore = false
      this.openOrderNextKey = ''
      try {
        const data = await this.requestOpenOrders('N', '')
        this.applyOpenOrderResponse(data, false)
      } catch (error) {
        this.openOrderItems = []
        this.openOrderErrorMessage = error && error.message ? error.message : '미체결 조회에 실패했습니다.'
      } finally {
        this.openOrderLoading = false
      }
    },
    async loadMoreOpenOrders() {
      if (!this.openOrderHasMore || !this.openOrderNextKey) return
      this.openOrderLoadingMore = true
      try {
        const data = await this.requestOpenOrders('Y', this.openOrderNextKey)
        this.applyOpenOrderResponse(data, true)
      } catch (error) {
        this.openOrderErrorMessage = error && error.message ? error.message : '미체결 추가 조회에 실패했습니다.'
      } finally {
        this.openOrderLoadingMore = false
      }
    },
    async requestOpenOrders(contYn, nextKey) {
      const response = await axios.post('/api/kiwoom/account/open-orders', {
        accountNo: this.selectedAccount,
        contYn,
        nextKey,
      })
      return this.parseApiResponse(response)
    },
    applyOpenOrderResponse(data, append) {
      const items = Array.isArray(data.items) ? data.items : []
      this.openOrderItems = append ? this.openOrderItems.concat(items) : items
      this.openOrderHasMore = !!data.hasMore
      this.openOrderNextKey = data.nextKey || ''
    },
    async fetchFilledOrders() {
      this.filledOrderLoading = true
      this.filledOrderErrorMessage = ''
      this.filledOrderHasMore = false
      this.filledOrderNextKey = ''
      try {
        const data = await this.requestFilledOrders('N', '')
        this.applyFilledOrderResponse(data, false)
      } catch (error) {
        this.filledOrderItems = []
        this.filledOrderErrorMessage = error && error.message ? error.message : '체결 조회에 실패했습니다.'
      } finally {
        this.filledOrderLoading = false
      }
    },
    async loadMoreFilledOrders() {
      if (!this.filledOrderHasMore || !this.filledOrderNextKey) return
      this.filledOrderLoadingMore = true
      try {
        const data = await this.requestFilledOrders('Y', this.filledOrderNextKey)
        this.applyFilledOrderResponse(data, true)
      } catch (error) {
        this.filledOrderErrorMessage = error && error.message ? error.message : '체결 추가 조회에 실패했습니다.'
      } finally {
        this.filledOrderLoadingMore = false
      }
    },
    async requestFilledOrders(contYn, nextKey) {
      const response = await axios.post('/api/kiwoom/account/filled-orders', {
        accountNo: this.selectedAccount,
        contYn,
        nextKey,
      })
      return this.parseApiResponse(response)
    },
    applyFilledOrderResponse(data, append) {
      const items = Array.isArray(data.items) ? data.items : []
      this.filledOrderItems = append ? this.filledOrderItems.concat(items) : items
      this.filledOrderHasMore = !!data.hasMore
      this.filledOrderNextKey = data.nextKey || ''
    },
    formatNumber(value) {
      const normalized = this.normalizeNumeric(value)
      if (normalized === '') return '-'
      const numeric = Number(normalized)
      if (Number.isNaN(numeric)) return value
      return numeric.toLocaleString()
    },
    formatSignedNumber(value) {
      const normalized = this.normalizeNumeric(value)
      if (normalized === '') return '-'
      const numeric = Number(normalized)
      if (Number.isNaN(numeric)) return value
      const sign = numeric > 0 ? '+' : ''
      return `${sign}${numeric.toLocaleString()}`
    },
    formatRate(value) {
      const normalized = this.normalizeNumeric(value)
      if (normalized === '') return '-'
      const numeric = Number(normalized)
      if (Number.isNaN(numeric)) return value
      const sign = numeric > 0 ? '+' : ''
      return `${sign}${numeric.toFixed(2)}%`
    },
    normalizeNumeric(value) {
      if (value === null || value === undefined) return ''
      const raw = String(value).trim()
      if (!raw) return ''
      let sign = ''
      let digits = raw
      if (raw.startsWith('+') || raw.startsWith('-')) {
        sign = raw[0]
        digits = raw.slice(1)
      }
      digits = digits.replace(/,/g, '')
      if (!digits) return ''
      return `${sign}${digits}`
    },
    rateClassByValue(value) {
      const numeric = Number(this.normalizeNumeric(value))
      if (Number.isNaN(numeric) || numeric === 0) return 'text-body'
      return numeric > 0 ? 'text-danger' : 'text-primary'
    },
    rateClassBySign(value) {
      const raw = String(value || '').trim()
      if (raw.startsWith('+')) return 'text-danger'
      if (raw.startsWith('-')) return 'text-primary'
      return 'text-body'
    },
  },
}
</script>

<style scoped>
.account-filter-row {
  gap: 0.75rem;
}

.daily-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
}

.summary-box {
  padding: 0.875rem 1rem;
  border: 1px solid rgba(115, 103, 240, 0.18);
  border-radius: 0.75rem;
  background: linear-gradient(180deg, rgba(115, 103, 240, 0.06), rgba(115, 103, 240, 0.02));
}
</style>
